import { useQuery } from '@tanstack/react-query';
import { apiGet } from './client';

/**
 * PUBLIC reseller-application status, looked up by the reference number shown on submission.
 *
 * Deliberately its own module and its own endpoint: the status page is reachable by someone
 * who has no account yet, so it must never call an admin endpoint (the previous page did,
 * which is why it hung forever on a 401 with no error state).
 */
export type PublicApplicationStatus = {
  id: string;
  status: 'pending' | 'in_review' | 'more_info_needed' | 'approved' | 'rejected' | 'withdrawn';
  orgName: string;
  applicantName: string;
  email: string;
  submittedAt: string | null;
  reviewedAt: string | null;
  timeline: Array<{
    at: string | null;
    kind: string;
    toStatus: string | null;
    text: string | null;
  }>;
};

export const getPublicApplicationStatus = (id: string) =>
  apiGet<PublicApplicationStatus>(`/reseller-applications/${encodeURIComponent(id)}/status`);

export const usePublicApplicationStatus = (id: string | null) =>
  useQuery({
    queryKey: ['public', 'reseller-application', id],
    queryFn: () => getPublicApplicationStatus(id!),
    enabled: !!id,
    // A wrong reference number is a permanent answer, not a transient failure — retrying a
    // 404 four times just makes the applicant stare at a spinner.
    retry: false,
  });
